<template>
  <section>
    <v-card class="pa-3 mb-4">
      <v-row align="center">
        <v-col cols="12" sm="4">
          <v-select
            v-model="localMode"
            :items="modeOptions"
            item-text="text"
            item-value="value"
            :label="$t('listSelector.selectMode')"
            @change="emitModeChange"
          />
        </v-col>
        <v-col cols="12" sm="4">
          <v-select
            v-model="localItemsPerPage"
            :items="itemsPerPageOptions"
            :label="$t('listSelector.itemsPerPage')"
            @change="emitItemsPerPageChange"
          />
        </v-col>
        <v-col cols="12" sm="4" v-if="error">
          <v-alert type="error" dense>{{ error }}</v-alert>
        </v-col>
      </v-row>
    </v-card>

    <v-row>
      <!-- 已套用 -->
      <v-col cols="12" md="6">
        <v-card outlined>
          <v-card-title>
            <v-row class="align-center" no-gutters>
              <v-col cols="6" class="text-left">
                {{ $t('listSelector.applied')}} {{ modeLabel }}
              </v-col>
              <v-col cols="6" class="text-right">
                <v-btn
                  small
                  color="primary"
                  @click="$emit('unapply-selected')"
                  :disabled="!localSelectedApplied.length"
                >
                {{ $t('listSelector.removeSelected')}} 
                </v-btn>
              </v-col>
            </v-row>
          </v-card-title>
          <br/>
          <v-card-text>
            <v-radio-group row 
              v-model="localAppliedSearchBy" 
              @change="onSearchByInput('applied', $event)"
              v-show="searchByOptions.length > 1">
              <template v-slot:label>
                <div>{{ $t('views.merchants.searchBy') }}<br></div>
              </template>
              <v-radio
                v-for="option in searchByOptions"
                :key="option.value"
                :label="option.text"
                :value="option.value"
              ></v-radio>
            </v-radio-group>
            <v-text-field
              :value="appliedSearch"
              :label="$t('listSelector.searchKeyword')"
              dense
              hide-details
              @input="onSearchInput('applied', $event)"
            />
            <v-data-table
              :headers="tableHeaders"
              :items="appliedList"
              item-key="id"
              show-select
              v-model="localSelectedApplied"
              :loading="loadingApplied"
              :server-items-length="appliedPagination.total"
              :page.sync="appliedPagination.page"
              :items-per-page="localItemsPerPage"
              hide-default-footer
              @update:page="fetchAppliedList"
              @input="onSelectedAppliedChange"
            >
              <template v-slot:item.actions="{ item }">
                <v-btn icon @click="$emit('unapply-one', item)">
                  <v-icon>mdi-minus</v-icon>
                </v-btn>
              </template>
            </v-data-table>

            <v-row class="d-flex align-center justify-space-between">
              <v-col cols="auto">
                <v-pagination
                  v-model="appliedPagination.page"
                  :length="appliedPageCount"
                  @input="fetchAppliedList"
                  :total-visible="5"
                />
              </v-col>
              <v-col cols="auto">
                <strong> {{ $t('listSelector.total') }}:</strong>  {{ appliedPagination.total || 0}}
              </v-col>
            </v-row>
            <v-row v-if="localSelectedApplied.length" class="d-flex align-center justify-space-between">
              <v-col cols="12">
                {{ $t('listSelector.selectedItems', { n: localSelectedApplied.length}) }}
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- 未套用 -->
      <v-col cols="12" md="6">
        <v-card outlined>
          <v-card-title>
            <v-row class="align-center" no-gutters>
              <v-col cols="6" class="text-left">
                {{ $t('listSelector.unapplied') }} {{ modeLabel }}
              </v-col>
              <v-col cols="6" class="text-right">
                <v-btn
                  small
                  color="primary"
                  @click="$emit('apply-selected')"
                  :disabled="!localSelectedUnapplied.length"
                >
                {{ $t('listSelector.applySelected') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-title>
          <br/>
          <v-card-text>
            <v-radio-group row 
              v-model="localUnappliedSearchBy" 
              v-if="searchByOptions.length > 1"
              @change="onSearchByInput('unapplied', $event)"
            >
              <template v-slot:label>
                <div>{{ $t('views.merchants.searchBy') }}<br></div>
              </template>
              <v-radio
                v-for="option in searchByOptions"
                :key="option.value"
                :label="option.text"
                :value="option.value"
              ></v-radio>
            </v-radio-group>
            <v-text-field
              :value="unappliedSearch"
              :label="$t('listSelector.searchKeyword')"
              dense
              hide-details
              @input="onSearchInput('unapplied', $event)"
            />
            <v-data-table
              :headers="tableHeaders"
              :items="unappliedList"
              item-key="id"
              show-select
              v-model="localSelectedUnapplied"
              :loading="loadingUnapplied"
              :server-items-length="unappliedPagination.total"
              :page.sync="unappliedPagination.page"
              :items-per-page="localItemsPerPage"
              hide-default-footer
              @update:page="fetchUnappliedList"
              @input="onSelectedUnappliedChange"
            >
              <template v-slot:item.actions="{ item }">
                <v-btn icon @click="$emit('apply-one', item)">
                  <v-icon> mdi-plus </v-icon>
                </v-btn>
              </template>
            </v-data-table>

            <v-row class="d-flex align-center justify-space-between">
              <v-col cols="auto">
                <v-pagination
                  v-model="unappliedPagination.page"
                  :length="unappliedPageCount"
                  @input="fetchUnappliedList"
                  :total-visible="5"
                />
              </v-col>
              <v-col cols="auto">
                <strong>{{$t('listSelector.total')}}:</strong> {{ unappliedPagination.total || 0}}
              </v-col>
            </v-row>
            <v-row v-if="localSelectedUnapplied.length" class="d-flex align-center justify-space-between">
              <v-col cols="12">
                {{ $t('listSelector.selectedItems', { n: localSelectedUnapplied.length}) }}
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </section>
</template>

<script>
export default {
  props: {
    modeOptions: Array,
    defaultMode: String,
    modeLabel: String,
    error: String,
    tableHeaders: Array,
  
    appliedList: Array,
    unappliedList: Array,
    selectedApplied: Array,
    selectedUnapplied: Array,
    appliedPagination: Object,
    unappliedPagination: Object,
    loadingApplied: Boolean,
    loadingUnapplied: Boolean,
    appliedSearch: String,
    unappliedSearch: String,
    searchByOptions: {
      type: Array,
      default() {
        return [];
      }
    },
    defaultSearchBy: String,
    appliedSearchBy: String,
    unappliedSearchBy: String,
    itemsPerPage: Number,

    itemsPerPageOptions: {
      type: Array,
      default() {
        return [10, 20, 30];
      }
    },

  },
  data() {
    return {
      debounceTimer: null,
      localMode: this.$props.defaultMode || "",
      localItemsPerPage: 10,
      localSelectedApplied: [],
      localSelectedUnapplied: [],
      localAppliedSearchBy: "",
      localUnappliedSearchBy: ""
    };
  },
  computed: {
    appliedPageCount() {
      return Math.ceil(this.appliedPagination.total / this.localItemsPerPage);
    },
    unappliedPageCount() {
      return Math.ceil(this.unappliedPagination.total / this.localItemsPerPage);
    }
  },
  watch: {
    itemsPerPage: {
      immediate: true,
      handler(val) {
        this.localItemsPerPage = val;
      }
    },
    modeOptions: {
      immediate: true,
      handler() {
        this.localMode = this.$props.defaultMode ?? this.$props.modeOptions?.[0]?.value ?? '';
        this.emitModeChange()
      }
    },
    selectedApplied: {
      immediate: true,
      handler(val) {
        this.localSelectedApplied = [...val];
      }
    },
    selectedUnapplied: {
      immediate: true,
      handler(val) {
        this.localSelectedUnapplied = [...val];
      }
    },
    // 當 mode 改變時，觸發重新評估 searchByOptions
    // localMode: {
    //   handler() {
    //     this.setInitialSearchByOptions();
    //     // 模式切換後，搜尋條件可能改變，強制觸發一次搜尋以更新列表
    //     this.onSearchInput('applied', this.appliedSearch);
    //     this.onSearchInput('unapplied', this.unappliedSearch);
    //   }
    // },
    searchByOptions: {
      immediate: true,
      handler(options) {
        this.setInitialSearchByOptions()
        // if (options.length && !this.localAppliedSearchBy) {
        //   this.localAppliedSearchBy = this.appliedSearchBy ?? this.defaultSearchBy ?? options[0].value;
        // }
        // if (options.length && !this.localUnappliedSearchBy) {
        //   this.localUnappliedSearchBy = this.unappliedSearchBy ?? this.defaultSearchBy ?? options[0].value;
        // }
      }
    }
  },
  methods: {
    emitModeChange() {
      this.$emit("update:mode", this.localMode);
      // 當 mode 改變時，觸發重新評估 searchByOptions
      this.setInitialSearchByOptions();
      this.$emit("mode-changed");
      this.$emit('update:selectedApplied', []);
      this.$emit('update:selectedUnapplied', []);
    },
    emitItemsPerPageChange() {
      this.$emit("update:itemsPerPage", this.localItemsPerPage);
      this.$emit("items-per-page-changed");
    },
    fetchAppliedList(page) {
      this.$emit("fetch-applied-list", page);
    },
    fetchUnappliedList(page) {
      this.$emit("fetch-unapplied-list", page);
    },
    onSearchInput(type, value) {
      const eventName = `update:${type}Search`;
      const searchByKey = type === 'applied' ? this.localAppliedSearchBy : this.localUnappliedSearchBy;
      
      this.$emit(eventName, {
        searchBy: value?.trim() ? searchByKey : '', // 有關鍵字時才回傳searchBy
        keyword: value?.trim()
      });

      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        const fetchFn = type === 'applied' ? this.fetchAppliedList : this.fetchUnappliedList;
        fetchFn(1); // 重設為第一頁
      }, 300);
    },
    onSearchByInput(type, value) {
      const eventName = `update:${type}Search`;
      const searchByKey = type === 'applied' ? this.localAppliedSearchBy : this.localUnappliedSearchBy;
      const searchKeyword = type === 'applied' ? this.appliedSearch : this.unappliedSearch;
      
      console.log('onSearchByInput', searchByKey);
      // 沒有填keyword時可以不用送，避免不必要的API 呼叫
      if (searchKeyword?.trim()) {
        this.$emit(eventName, {
          searchBy: searchKeyword?.trim() ? searchByKey : '', // 有關鍵字時才回傳searchBy
          keyword: searchKeyword?.trim()
        });

        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
          const fetchFn = type === 'applied' ? this.fetchAppliedList : this.fetchUnappliedList;
          fetchFn(1); // 重設為第一頁
        }, 300);
      }
     
    },
    onSelectedAppliedChange(val) {
      this.localSelectedApplied = val;
      this.$emit('update:selectedApplied', val);
    },
    onSelectedUnappliedChange(val) {
      this.localSelectedUnapplied = val;
      this.$emit('update:selectedUnapplied', val);
    },
    setInitialSearchByOptions() {
      if (this.searchByOptions.length) {
        // 設定已套用列表的搜尋選項
        const defaultAppliedValue = this.appliedSearchBy || this.defaultSearchBy || this.searchByOptions[0].value;
        // 如果當前的 localAppliedSearchBy 不在新的 searchByOptions 中，或為空值，則重設
        if (!this.searchByOptions.some(option => option.value === this.localAppliedSearchBy) || !this.localAppliedSearchBy) {
             this.localAppliedSearchBy = defaultAppliedValue;
        }

        // 設定未套用列表的搜尋選項
        const defaultUnappliedValue = this.unappliedSearchBy ?? this.defaultSearchBy ?? this.searchByOptions[0].value;
        // 如果當前的 localUnappliedSearchBy 不在新的 searchByOptions 中，或為空值，則重設
        if (!this.searchByOptions.some(option => option.value === this.localUnappliedSearchBy) || !this.localUnappliedSearchBy) {
             this.localUnappliedSearchBy = defaultUnappliedValue;
        }
      } else {
        // 如果沒有可用的 searchByOptions，則清空搜尋選項
        this.localAppliedSearchBy = "";
        this.localUnappliedSearchBy = "";
      }
    }
  }
};
</script>
